import React, { useRef, useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import * as VisuallyHidden from "@radix-ui/react-visually-hidden";
import { Download, Share2, Loader2, X, Sparkles, Flame } from "lucide-react";
import { toBlob, toPng } from "html-to-image";
import { toast } from "sonner";

const TONE = {
  pink: "#FE0979",
  cyan: "#00F2FE",
  emerald: "#34D399",
  amber: "#F59E0B",
  zinc: "#A1A1AA",
};

function ringColor(label) {
  if (!label) return TONE.cyan;
  const l = label.toLowerCase();
  if (l.includes("magnet")) return TONE.pink;
  if (l.includes("trending")) return TONE.cyan;
  if (l.includes("creator")) return TONE.emerald;
  if (l.includes("niche")) return TONE.amber;
  return TONE.zinc;
}

/**
 * Visual-only share card (9:16). Used inside ShareCardDialog.
 * Sized at 540x960 in DOM so html-to-image PNG is high-res.
 */
export const ShareCard = React.forwardRef(function ShareCardImpl({ itinerary }, ref) {
  const top3 = (itinerary.top_viral_spots && itinerary.top_viral_spots.length
    ? itinerary.top_viral_spots
    : itinerary.spots || []
  ).slice(0, 3);
  const avg = top3.length
    ? Math.round(top3.reduce((s, x) => s + (x?.viral_breakdown?.score ?? x?.viral_score ?? 0), 0) / top3.length)
    : (itinerary?.viral_breakdown?.score ?? itinerary.viral_score ?? 90);
  const stroke = ringColor(itinerary?.viral_breakdown?.label);
  const tags = (itinerary.vibe_tags || []).slice(0, 4);
  const dest = itinerary.destination_recommendation || itinerary.destination || "";
  const message = itinerary.subtitle || itinerary.vibe_summary || "AI-curated viral travel itinerary for creators.";

  const radius = 70, sw = 10;
  const C = 2 * Math.PI * radius;
  const offset = C * (1 - avg / 100);

  return (
    <div
      ref={ref}
      data-testid="share-card-canvas"
      style={{
        width: 540,
        height: 960,
        position: "relative",
        overflow: "hidden",
        borderRadius: 36,
        backgroundColor: "#050505",
        color: "#fff",
        fontFamily: '"Manrope", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      }}
    >
      <img
        src={itinerary.cover_image}
        alt=""
        crossOrigin="anonymous"
        style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}
      />
      {/* dark scrim */}
      <div style={{ position: "absolute", inset: 0, background: "linear-gradient(180deg, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0.15) 30%, rgba(0,0,0,0.85) 100%)" }} />
      {/* color glows */}
      <div style={{ position: "absolute", top: -120, left: -80, width: 360, height: 360, borderRadius: "50%", background: "rgba(254,9,121,0.32)", filter: "blur(80px)" }} />
      <div style={{ position: "absolute", bottom: -120, right: -80, width: 360, height: 360, borderRadius: "50%", background: "rgba(0,242,254,0.28)", filter: "blur(80px)" }} />

      {/* top brand */}
      <div style={{ position: "absolute", top: 30, left: 30, right: 30, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 28, height: 28, borderRadius: "50%", background: "linear-gradient(135deg,#FE0979,#00F2FE)" }}>
            <span style={{ width: 22, height: 22, borderRadius: "50%", background: "#000", display: "inline-flex", alignItems: "center", justifyContent: "center", fontWeight: 900, fontSize: 12, color: "#fff", letterSpacing: -0.5, fontFamily: '"Outfit", sans-serif' }}>V</span>
          </span>
          <span style={{ fontFamily: '"Outfit", sans-serif', fontWeight: 900, fontSize: 16, letterSpacing: -0.4 }}>VYBE</span>
        </div>
        <span style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: 3, textTransform: "uppercase", color: "rgba(255,255,255,0.85)", background: "rgba(255,255,255,0.10)", border: "1px solid rgba(255,255,255,0.18)", padding: "5px 10px", borderRadius: 999, backdropFilter: "blur(12px)" }}>
          AI · CREATOR TRIP
        </span>
      </div>

      {/* destination kicker */}
      <div style={{ position: "absolute", top: 110, left: 30, right: 30 }}>
        <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: 4, textTransform: "uppercase", color: "#00F2FE", marginBottom: 8 }}>{dest}</div>
        <div style={{ fontFamily: '"Outfit", sans-serif', fontWeight: 900, fontSize: 50, lineHeight: 0.95, letterSpacing: -1.5, textShadow: "0 4px 24px rgba(0,0,0,0.55)" }}>
          {itinerary.title}
        </div>
      </div>

      {/* avg viral score ring */}
      <div style={{ position: "absolute", top: 360, left: 30, display: "flex", alignItems: "center", gap: 18 }}>
        <div style={{ position: "relative", width: 170, height: 170 }}>
          <svg width={170} height={170} style={{ transform: "rotate(-90deg)" }}>
            <circle cx={85} cy={85} r={radius} fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth={sw} />
            <circle cx={85} cy={85} r={radius} fill="none" stroke={stroke} strokeWidth={sw} strokeLinecap="round" strokeDasharray={C} strokeDashoffset={offset} style={{ filter: `drop-shadow(0 0 8px ${stroke}88)` }} />
          </svg>
          <div style={{ position: "absolute", inset: 0, display: "grid", placeItems: "center" }}>
            <div style={{ textAlign: "center" }}>
              <div style={{ fontFamily: '"Outfit", sans-serif', fontWeight: 900, fontSize: 44, color: stroke, lineHeight: 1 }}>{avg}</div>
              <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: 3, textTransform: "uppercase", color: "rgba(255,255,255,0.7)", marginTop: 6 }}>Avg Viral</div>
            </div>
          </div>
        </div>
        <div style={{ flex: 1 }}>
          {itinerary?.viral_breakdown?.label && (
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 3, textTransform: "uppercase", color: stroke, marginBottom: 6 }}>{itinerary.viral_breakdown.label}</div>
          )}
          <div style={{ fontSize: 14, lineHeight: 1.45, color: "rgba(255,255,255,0.92)", fontFamily: '"Manrope", sans-serif' }}>
            {message.length > 110 ? message.slice(0, 109) + "…" : message}
          </div>
        </div>
      </div>

      {/* vibe tags */}
      <div style={{ position: "absolute", top: 555, left: 30, right: 30, display: "flex", flexWrap: "wrap", gap: 6 }}>
        {tags.map((t) => (
          <span key={t} style={{ fontSize: 10, fontWeight: 700, letterSpacing: 2.4, textTransform: "uppercase", padding: "6px 11px", border: "1px solid rgba(255,255,255,0.22)", borderRadius: 999, background: "rgba(255,255,255,0.10)" }}>
            {t}
          </span>
        ))}
      </div>

      {/* top 3 spots */}
      <div style={{ position: "absolute", top: 615, left: 30, right: 30 }}>
        <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: 4, textTransform: "uppercase", color: "#FE0979", marginBottom: 12, display: "flex", alignItems: "center", gap: 6 }}>
          🔥 Top 3 viral spots
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {top3.map((s, i) => (
            <div key={s.id || i} style={{ display: "flex", alignItems: "center", gap: 12, background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.14)", borderRadius: 18, padding: 10, backdropFilter: "blur(12px)" }}>
              <div style={{ fontFamily: '"Outfit", sans-serif', fontWeight: 900, fontSize: 22, color: "rgba(255,255,255,0.55)", width: 24, textAlign: "center" }}>{i + 1}</div>
              {s.image && (
                <img src={s.image} alt="" crossOrigin="anonymous" style={{ width: 56, height: 56, borderRadius: 12, objectFit: "cover" }} />
              )}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700, fontSize: 16, lineHeight: 1.15, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{s.name}</div>
                <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 2, textTransform: "uppercase", color: "rgba(255,255,255,0.6)", marginTop: 3 }}>{s.city || ""}</div>
              </div>
              <div style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "5px 9px", borderRadius: 999, background: "rgba(0,0,0,0.55)", border: `1px solid ${ringColor(s?.viral_breakdown?.label)}55` }}>
                <span style={{ color: ringColor(s?.viral_breakdown?.label), fontSize: 11 }}>🔥</span>
                <span style={{ fontFamily: '"Outfit", sans-serif', fontWeight: 800, fontSize: 13, color: ringColor(s?.viral_breakdown?.label) }}>
                  {s?.viral_breakdown?.score ?? s?.viral_score ?? "-"}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* footer signature */}
      <div style={{ position: "absolute", bottom: 28, left: 30, right: 30, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div>
          <div style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: 4, textTransform: "uppercase", color: "rgba(255,255,255,0.55)" }}>Made with</div>
          <div style={{ fontFamily: '"Outfit", sans-serif', fontWeight: 900, fontSize: 22, letterSpacing: -0.5, background: "linear-gradient(135deg,#fff 0%, #FE0979 50%, #00F2FE 100%)", WebkitBackgroundClip: "text", backgroundClip: "text", color: "transparent" }}>VYBE AI</div>
        </div>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 2, textTransform: "uppercase", color: "rgba(255,255,255,0.7)" }}>find your viral trip</div>
      </div>
    </div>
  );
});

export const ShareCardDialog = ({ itinerary, open, onOpenChange }) => {
  const cardRef = useRef(null);
  const [busyDl, setBusyDl] = useState(false);
  const [busyShare, setBusyShare] = useState(false);

  const filename = `vybe-${(itinerary?.id || "trip").slice(0, 24)}.png`;

  const download = async () => {
    if (!cardRef.current || busyDl) return;
    setBusyDl(true);
    try {
      const dataUrl = await toPng(cardRef.current, { cacheBust: true, pixelRatio: 2, backgroundColor: "#050505", skipFonts: true });
      const a = document.createElement("a");
      a.href = dataUrl; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      toast.success("Card saved to your downloads");
    } catch (e) {
      toast.error("Couldn't render the card. Try again.");
    } finally {
      setBusyDl(false);
    }
  };

  const share = async () => {
    if (!cardRef.current || busyShare) return;
    setBusyShare(true);
    try {
      const blob = await toBlob(cardRef.current, { cacheBust: true, pixelRatio: 2, backgroundColor: "#050505", skipFonts: true });
      if (!blob) throw new Error("no blob");
      const file = new File([blob], filename, { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: itinerary.title, text: "Made on VYBE 🌐✨" });
        toast.success("Shared");
      } else {
        // Fallback: download instead
        await download();
      }
    } catch (e) {
      if (e?.name !== "AbortError") toast.error("Share failed — saved as download instead.");
    } finally {
      setBusyShare(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="share-card-dialog"
        className="bg-transparent border-0 p-0 max-w-md overflow-hidden"
      >
        <VisuallyHidden.Root>
          <DialogTitle>Share card preview</DialogTitle>
          <DialogDescription>Download or share an Instagram-story-style card of this trip.</DialogDescription>
        </VisuallyHidden.Root>
        <div className="glass-strong rounded-3xl border border-white/10 overflow-hidden">
          <div className="px-4 py-3 border-b border-white/5 flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <Sparkles className="h-3.5 w-3.5 text-[#00F2FE]" />
              <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-300">Share card · 9:16</span>
            </div>
            <button
              onClick={() => onOpenChange(false)}
              data-testid="share-card-close"
              className="text-zinc-400 hover:text-white transition"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="p-4 grid place-items-center bg-black/30">
            <div style={{ transform: "scale(0.62)", transformOrigin: "top center", width: 540, height: 960 }}>
              <ShareCard ref={cardRef} itinerary={itinerary} />
            </div>
          </div>

          <div className="p-4 pt-0 flex gap-2">
            <button
              type="button"
              onClick={download}
              disabled={busyDl}
              data-testid="share-card-download"
              className="flex-1 inline-flex items-center justify-center gap-2 gradient-pink-cyan text-black font-display font-bold uppercase tracking-[0.18em] text-xs rounded-full py-3 hover:opacity-95 active:scale-[0.99] transition disabled:opacity-70"
            >
              {busyDl ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              {busyDl ? "Rendering" : "Download PNG"}
            </button>
            <button
              type="button"
              onClick={share}
              disabled={busyShare}
              data-testid="share-card-native"
              className="flex-1 inline-flex items-center justify-center gap-2 glass text-white font-display font-bold uppercase tracking-[0.18em] text-xs rounded-full py-3 hover:bg-white/10 active:scale-[0.99] transition disabled:opacity-70"
            >
              {busyShare ? <Loader2 className="h-4 w-4 animate-spin" /> : <Share2 className="h-4 w-4" />}
              {busyShare ? "Sharing" : "Share"}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const ShareCardButton = ({ itinerary, className = "" }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="share-card-btn"
        className={`glass text-white font-display font-bold uppercase tracking-[0.18em] text-xs rounded-full px-5 py-3 inline-flex items-center gap-2 hover:bg-white/10 active:scale-[0.98] transition ${className}`}
      >
        <Flame className="h-4 w-4 text-[#FE0979]" /> Share card
      </button>
      {open && <ShareCardDialog itinerary={itinerary} open={open} onOpenChange={setOpen} />}
    </>
  );
};
